import { useState, useEffect } from 'react';

const STORAGE_KEY = 'colorScheme';
const DARK_CLASS = 'dark-mode';

/**
 * useColorScheme — manages light/dark mode preference.
 *
 * Priority order:
 *   1. User's explicit choice (localStorage)
 *   2. System preference (prefers-color-scheme media query)
 *   3. Default: light
 *
 * Applies 'dark-mode' class to <html> element.
 * Use CSS variables in your stylesheet:
 *   :root { --bg: #fff; --text: #111; }
 *   html.dark-mode { --bg: #111; --text: #fff; }
 *
 * @returns {{ scheme, toggle, setScheme, isSystemDark }}
 */
const useColorScheme = () => {
  const getSystemDark = () =>
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  const getSaved = () => {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY); // 'light' | 'dark' | null
  };

  const getInitial = () => {
    const saved = getSaved();
    if (saved) return saved;
    return getSystemDark() ? 'dark' : 'light';
  };

  const [scheme, setSchemeState] = useState(getInitial);
  const [isSystemDark, setIsSystemDark] = useState(getSystemDark);

  // Apply/remove dark class
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (scheme === 'dark') {
      document.documentElement.classList.add(DARK_CLASS);
    } else {
      document.documentElement.classList.remove(DARK_CLASS);
    }
  }, [scheme]);

  // Listen for system preference changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = e => {
      setIsSystemDark(e.matches);
      // Only follow system if no explicit preference saved
      if (!getSaved()) {
        setSchemeState(e.matches ? 'dark' : 'light');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const setScheme = (newScheme) => {
    setSchemeState(newScheme);
    if (typeof localStorage !== 'undefined') {
      if (newScheme === null) {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, newScheme);
      }
    }
  };

  const toggle = () => setScheme(scheme === 'dark' ? 'light' : 'dark');

  return { scheme, toggle, setScheme, isSystemDark, isDark: scheme === 'dark' };
};

export default useColorScheme;
